import React, { useEffect, useState, useRef } from "react";
import { Seo } from "../components/Seo";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, HeartHandshake, CheckCircle2, XCircle, Share2, BadgeCheck } from "lucide-react";

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

export function MicrositeThanks({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get("session_id");
    if (!sessionId) { setState("error"); return; }
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/public/donations/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current < 8) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [location.search]);

  return (
    <div className="min-h-screen bg-app text-white flex items-center justify-center p-6">
      <div className="glass-card rounded-lg p-10 max-w-md text-center" data-testid="donation-thanks-card">
        {state === "checking" && (<><CheckCircle2 className="w-12 h-12 text-emerald-300/70 mx-auto" /><div className="mt-4 font-display text-2xl font-bold">Thank you!</div><p className="text-sm text-slate-400 mt-2 flex items-center justify-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300" /> Finalizing your receipt…</p></>)}
        {state === "paid" && (<><CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto" /><div className="mt-4 font-display text-2xl font-bold">Thank you!</div><p className="text-sm text-slate-400 mt-2">Your contribution was received. A receipt has been emailed to you.</p></>)}
        {state === "pending" && (<><Loader2 className="w-10 h-10 text-amber-300 mx-auto" /><div className="mt-4 font-display text-xl font-bold">Payment processing</div><p className="text-sm text-slate-400 mt-2">Your payment is still processing — you'll receive an email receipt once complete.</p></>)}
        {(state === "failed" || state === "error") && (<><XCircle className="w-12 h-12 text-rose-300 mx-auto" /><div className="mt-4 font-display text-xl font-bold">Payment not completed</div><p className="text-sm text-slate-400 mt-2">Your payment didn't go through. You can try again.</p></>)}
        <button onClick={() => navigate(slugOverride ? "/" : `/m/${slug}`)} className="mt-6 text-sm text-cyan-300 hover:underline" data-testid="back-to-microsite-btn">← Back to campaign page</button>
      </div>
    </div>
  );
}

export default function Microsite({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const location = useLocation();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [donor, setDonor] = useState({ name: "", email: "", employer: "", occupation: "", state: "" });
  const [att, setAtt] = useState({ us_citizen: false, own_funds: false, not_federal_contractor: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/microsites/${slug}`).then(r => setData(r.data)).catch(() => setNotFound(true));
    if (new URLSearchParams(location.search).get("canceled")) toast.info("Checkout canceled — no charge was made");
  }, [slug]); // eslint-disable-line

  if (notFound) return <div className="min-h-screen bg-app text-white flex items-center justify-center"><div className="text-slate-400">This page doesn't exist or is unpublished.</div></div>;
  if (!data) return <div className="min-h-screen bg-app text-white flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-300 animate-spin" /></div>;

  const { page, campaign } = data;
  const blocks = page?.blocks || [];
  const seoTitle = campaign?.name || page?.title || page?.headline || `Support ${data.entity?.name || "the campaign"}`;
  const theme = page?.theme || {};
  const primary = data.entity?.accent_color || theme.primary || "#fbbf24";
  const donationBlock = blocks.find(b => b.type === "donation_form")?.content || {};
  const suggested = donationBlock.suggested || campaign?.suggested_amounts || [10, 25, 50, 100, 250];
  const finalAmount = custom ? parseFloat(custom) : amount;

  const donate = async () => {
    if (!donor.name || !donor.email || !donor.employer || !donor.occupation) {
      toast.error("Name, email, employer and occupation are required by federal law"); return;
    }
    if (!(att.us_citizen && att.own_funds && att.not_federal_contractor)) {
      toast.error("Please confirm all eligibility statements"); return;
    }
    if (!finalAmount || finalAmount < 1) { toast.error("Enter a valid amount"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/public/donations/checkout", {
        fundraising_campaign_id: campaign.id,
        microsite_slug: slug,
        amount: finalAmount,
        recurring,
        donor,
        attestations: att,
        origin_url: window.location.origin,
        return_base: slugOverride ? window.location.origin : `${window.location.origin}/m/${slug}`,
        utm_source: new URLSearchParams(location.search).get("utm_source"),
        utm_campaign: new URLSearchParams(location.search).get("utm_campaign"),
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const render = (b, i) => {
    const c = b.content || {};
    switch (b.type) {
      case "hero":
        return (
          <section key={i} className="pt-20 pb-14 text-center px-6" data-testid="ms-hero">
            {data.entity?.verified && (
              <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 text-xs font-semibold"
                data-testid="ms-verified-badge">
                <BadgeCheck className="w-3.5 h-3.5" /> Verified Campaign
              </div>
            )}
            {c.eyebrow && <div className="text-xs uppercase tracking-[0.3em] font-medium mb-4" style={{ color: primary }}>{c.eyebrow}</div>}
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl mx-auto">{c.headline}</h1>
            {c.subheadline && <p className="mt-4 text-base text-slate-300 max-w-2xl mx-auto">{c.subheadline}</p>}
            <a href="#donate" className="inline-block mt-8 px-8 py-3 rounded-full font-semibold text-slate-900 transition-transform hover:scale-105" style={{ background: primary }} data-testid="ms-hero-cta">
              {donationBlock.cta || campaign?.cta_label || "Contribute"}
            </a>
          </section>
        );
      case "progress_bar": {
        const goal = c.goal || campaign?.goal_amount || 0;
        const raised = campaign?.raised_amount || 0;
        const pct = goal ? Math.min(100, raised / goal * 100) : 0;
        return (
          <section key={i} className="max-w-2xl mx-auto px-6 py-6" data-testid="ms-progress">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-300">{c.label || "Fundraising goal"}</span>
              <span className="font-mono text-white">{usd(raised)} / {usd(goal)}</span>
            </div>
            <div className="h-3 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: primary }} />
            </div>
            <div className="mt-1.5 text-xs text-slate-500">{campaign?.total_donors || 0} donors · {pct.toFixed(0)}% of goal</div>
          </section>
        );
      }
      case "story":
        return (
          <section key={i} className="max-w-2xl mx-auto px-6 py-10" data-testid="ms-story">
            <h2 className="font-display text-lg md:text-lg font-bold mb-3" style={{ color: primary }}>{c.title}</h2>
            <p className="text-slate-300 leading-relaxed whitespace-pre-wrap text-sm">{c.body}</p>
          </section>
        );
      case "priorities":
        return (
          <section key={i} className="max-w-4xl mx-auto px-6 py-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(c.items || []).map((it, j) => (
                <div key={j} className="glass-card rounded-lg p-5">
                  <div className="font-display font-bold text-sm">{it.title}</div>
                  <p className="text-xs text-slate-400 mt-1.5">{it.body}</p>
                </div>
              ))}
            </div>
          </section>
        );
      case "endorsements":
        return (
          <section key={i} className="max-w-2xl mx-auto px-6 py-8 text-center">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Endorsed by</div>
            <div className="flex flex-wrap justify-center gap-2">
              {(c.items || []).map((e, j) => <span key={j} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300">{e}</span>)}
            </div>
          </section>
        );
      case "donation_form":
        return (
          <section key={i} id="donate" className="max-w-xl mx-auto px-6 py-12" data-testid="ms-donation-form">
            <div className="glass-card rounded-lg p-6 border" style={{ borderColor: `${primary}44` }}>
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-display text-lg font-bold flex items-center gap-2"><HeartHandshake className="w-5 h-5" style={{ color: primary }} /> {c.cta || "Contribute"}</h2>
                {data.entity?.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-1 shrink-0"
                    data-testid="ms-donation-verified-badge">
                    <BadgeCheck className="w-3 h-3" /> Verified Campaign
                  </span>
                )}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {suggested.map(a => (
                  <button key={a} onClick={() => { setAmount(a); setCustom(""); }} data-testid={`ms-amount-${a}`}
                    className={`py-2.5 rounded-md border text-sm font-semibold transition-colors ${!custom && amount === a ? "text-slate-900" : "text-white border-white/15 hover:border-white/30"}`}
                    style={!custom && amount === a ? { background: primary, borderColor: primary } : {}}>
                    ${a}
                  </button>
                ))}
              </div>
              <input value={custom} onChange={e => setCustom(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Custom amount"
                className="mt-2 w-full bg-slate-900/70 border border-white/15 rounded-md px-3 py-2.5 text-sm" data-testid="ms-custom-amount" />
              {(c.recurring_enabled ?? true) && (
                <label className="mt-3 flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} data-testid="ms-recurring-toggle" />
                  Make this monthly
                </label>
              )}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <input value={donor.name} onChange={e => setDonor({ ...donor, name: e.target.value })} placeholder="Full name *" className="bg-slate-900/70 border border-white/15 rounded-md px-3 py-2.5 text-sm col-span-2" data-testid="ms-donor-name" />
                <input value={donor.email} onChange={e => setDonor({ ...donor, email: e.target.value })} placeholder="Email *" className="bg-slate-900/70 border border-white/15 rounded-md px-3 py-2.5 text-sm col-span-2" data-testid="ms-donor-email" />
                <input value={donor.employer} onChange={e => setDonor({ ...donor, employer: e.target.value })} placeholder="Employer *" className="bg-slate-900/70 border border-white/15 rounded-md px-3 py-2.5 text-sm" data-testid="ms-donor-employer" />
                <input value={donor.occupation} onChange={e => setDonor({ ...donor, occupation: e.target.value })} placeholder="Occupation *" className="bg-slate-900/70 border border-white/15 rounded-md px-3 py-2.5 text-sm" data-testid="ms-donor-occupation" />
              </div>
              <div className="mt-4 space-y-1.5 text-xs text-slate-400">
                {[
                  ["us_citizen", "I am a U.S. citizen or lawfully admitted permanent resident."],
                  ["own_funds", "This contribution is made from my own funds, not those of another."],
                  ["not_federal_contractor", "I am not a federal contractor."],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-start gap-2">
                    <input type="checkbox" checked={att[k]} onChange={e => setAtt({ ...att, [k]: e.target.checked })} className="mt-0.5" data-testid={`ms-att-${k}`} />
                    {label}
                  </label>
                ))}
              </div>
              <button onClick={donate} disabled={submitting} data-testid="ms-donate-btn"
                className="mt-5 w-full py-3 rounded-full font-bold text-slate-900 transition-transform hover:scale-[1.02] disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ background: primary }}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {recurring ? `Contribute ${finalAmount ? usd(finalAmount) : ""} monthly` : `Contribute ${finalAmount ? usd(finalAmount) : ""}`}
              </button>
              <div className="mt-2 text-[10px] text-slate-600 text-center">Secure payment via Stripe · Contributions are not tax deductible</div>
            </div>
          </section>
        );
      case "social_share":
        return (
          <section key={i} className="text-center py-8">
            <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }}
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="ms-share-btn">
              <Share2 className="w-4 h-4" /> {c.headline || "Share this page"}
            </button>
          </section>
        );
      case "disclaimer":
        return (
          <footer key={i} className="border-t border-white/10 py-6 text-center px-6" data-testid="ms-disclaimer">
            <p className="text-[11px] text-slate-500 max-w-xl mx-auto">{c.text || campaign?.disclaimer}</p>
          </footer>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-app text-white" data-testid="microsite-page">
      <Seo title={seoTitle} description={(page?.subheadline || campaign?.description || `Contribute to ${data.entity?.name || "the campaign"} — secure, FEC-compliant checkout on TotalReach.`).slice(0, 160)} />
      {blocks.map(render)}
      {!blocks.some(b => b.type === "disclaimer") && (
        <footer className="border-t border-white/10 py-6 text-center px-6">
          <p className="text-[11px] text-slate-500">{campaign?.disclaimer || `Paid for by ${data.entity?.committee_name || data.entity?.name || "the campaign"}.`}</p>
        </footer>
      )}
    </div>
  );
}
